import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileDown } from "lucide-react";
import { ClientIdentity } from "@/components/ClientBrand";
import { api, errMsg, label as lbl } from "@/lib/api";

export const STATUS_STYLES = {
  // projects
  discovery: "bg-sky-50 text-sky-700 border-sky-200", proposal: "bg-violet-50 text-violet-700 border-violet-200",
  in_progress: "bg-blue-50 text-blue-700 border-blue-200", review: "bg-amber-50 text-amber-700 border-amber-200",
  delivered: "bg-emerald-50 text-emerald-700 border-emerald-200", on_hold: "bg-slate-100 text-slate-600 border-slate-200",
  cancelled: "bg-rose-50 text-rose-700 border-rose-200",
  // tasks
  todo: "bg-slate-100 text-slate-700 border-slate-200", done: "bg-emerald-50 text-emerald-700 border-emerald-200",
  // proposals/invoices
  draft: "bg-slate-100 text-slate-600 border-slate-200", sent: "bg-blue-50 text-blue-700 border-blue-200",
  accepted: "bg-emerald-50 text-emerald-700 border-emerald-200", rejected: "bg-rose-50 text-rose-700 border-rose-200",
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200", partial: "bg-amber-50 text-amber-700 border-amber-200",
  overdue: "bg-rose-50 text-rose-700 border-rose-200",
  // clients
  lead: "bg-violet-50 text-violet-700 border-violet-200", active: "bg-emerald-50 text-emerald-700 border-emerald-200", inactive: "bg-slate-100 text-slate-500 border-slate-200",
  // priority
  low: "bg-slate-100 text-slate-600 border-slate-200", medium: "bg-sky-50 text-sky-700 border-sky-200",
  high: "bg-amber-50 text-amber-700 border-amber-200", urgent: "bg-rose-50 text-rose-700 border-rose-200",
  // assets / work orders / content
  pending: "bg-amber-50 text-amber-700 border-amber-200", approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  changes_requested: "bg-rose-50 text-rose-700 border-rose-200", in_production: "bg-amber-50 text-amber-700 border-amber-200",
  requested: "bg-violet-50 text-violet-700 border-violet-200", quoted: "bg-violet-50 text-violet-700 border-violet-200",
  in_review: "bg-amber-50 text-amber-700 border-amber-200", closed: "bg-slate-100 text-slate-600 border-slate-200", declined: "bg-rose-50 text-rose-700 border-rose-200",
  idea: "bg-slate-100 text-slate-600 border-slate-200", drafting: "bg-sky-50 text-sky-700 border-sky-200", ready_for_review: "bg-amber-50 text-amber-700 border-amber-200",
  scheduled: "bg-blue-50 text-blue-700 border-blue-200", published: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export function StatusBadge({ status, className = "", testId }) {
  return (
    <span data-testid={testId} className={`inline-flex items-center px-2 py-0.5 rounded border text-[11px] font-semibold tracking-wide uppercase ${STATUS_STYLES[status] || "bg-slate-100 text-slate-600 border-slate-200"} ${className}`}>
      {lbl(status)}
    </span>
  );
}

export function PageHeader({ eyebrow, title, subtitle, actions }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 fade-up">
      <div>
        {eyebrow && <div className="overline-label mb-2">{eyebrow}</div>}
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-1.5 max-w-xl">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

export function StatCard({ label, value, hint, tone = "default", icon: Icon, testId }) {
  const tones = { default: "text-slate-900", good: "text-emerald-600", warn: "text-amber-600", bad: "text-rose-600", accent: "text-blue-600" };
  return (
    <div className="panel panel-hover p-5 h-full relative overflow-hidden" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className="overline-label">{label}</div>
        {Icon && <Icon size={16} className="text-slate-400" />}
      </div>
      <div className={`num text-2xl sm:text-3xl font-semibold mt-3 ${tones[tone]}`}>{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-1.5">{hint}</div>}
    </div>
  );
}

export function Progress({ value, tone = "bg-blue-600", className = "" }) {
  return (
    <div className={`h-1.5 w-full bg-slate-100 rounded-full overflow-hidden ${className}`}>
      <div className={`h-full ${tone} rounded-full transition-[width] duration-500`} style={{ width: `${Math.min(100, Math.max(0, value || 0))}%` }} />
    </div>
  );
}

export function Avatar({ name = "", src, size = 28 }) {
  const initials = name.split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase() || "?";
  if (src) return <img src={src} alt={name} className="rounded-full object-cover shrink-0" style={{ width: size, height: size }} />;
  return (
    <div className="rounded-full bg-slate-800 text-white grid place-items-center shrink-0 font-semibold" style={{ width: size, height: size, fontSize: size * 0.36 }}>
      {initials}
    </div>
  );
}

export function EmptyState({ title, hint, action, testId }) {
  return (
    <div className="panel border-dashed p-10 text-center" data-testid={testId}>
      <div className="font-heading font-semibold text-slate-800">{title}</div>
      {hint && <p className="text-sm text-slate-500 mt-1">{hint}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Section({ title, action, children, className = "", testId }) {
  return (
    <section className={`panel p-5 sm:p-6 ${className}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-semibold text-slate-900">{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

export function Field({ f, value, onChange }) {
  const id = `field-${f.name}`;
  const common = { id, "data-testid": `input-${f.name}`, placeholder: f.placeholder };
  let control;
  if (f.type === "textarea") control = <Textarea {...common} rows={f.rows || 3} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
  else if (f.type === "select")
    control = (
      <Select value={value ?? ""} onValueChange={onChange}>
        <SelectTrigger id={id} className={f.options.some((o) => o.client) ? "min-h-10 h-auto text-left" : ""} data-testid={`select-${f.name}`}><SelectValue placeholder={f.placeholder || "Select…"}>{f.options.find((o) => o.value === value)?.client ? <ClientIdentity client={f.options.find((o) => o.value === value).client} size={24} testId={`selected-${f.name}-brand`} /> : undefined}</SelectValue></SelectTrigger>
        <SelectContent>{f.options.map((o) => <SelectItem key={o.value} value={o.value} textValue={o.label} className={o.client ? "focus:bg-slate-100 focus:text-slate-900" : ""} data-testid={`option-${f.name}-${o.value}`}>{o.client ? <ClientIdentity client={o.client} testId={`option-${f.name}-${o.value}-brand`} /> : o.label}</SelectItem>)}</SelectContent>
      </Select>
    );
  else if (f.type === "multi")
    control = (
      <div className="flex flex-wrap gap-1.5" data-testid={`multi-${f.name}`}>
        {f.options.map((o) => { const on = (value || []).includes(o); return <button type="button" key={o} onClick={() => onChange(on ? value.filter((x) => x !== o) : [...(value || []), o])} className={`px-2.5 py-1 rounded-full text-xs font-medium border capitalize transition-colors ${on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`} data-testid={`multi-${f.name}-${o}`}>{o}</button>; })}
      </div>
    );
  else if (f.type === "assets")
    control = (
      <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto" data-testid={`assets-${f.name}`}>
        {f.options.map((a) => { const on = (value || []).includes(a.id); return <button type="button" key={a.id} title={a.name} onClick={() => onChange(on ? value.filter((x) => x !== a.id) : [...(value || []), a.id])} className={`h-14 w-14 rounded-md overflow-hidden border-2 ${on ? "border-blue-600" : "border-transparent"}`} data-testid={`pick-asset-${a.id}`}>{a.thumb_url ? <img src={`${process.env.REACT_APP_BACKEND_URL}${a.thumb_url}`} alt="" className="h-full w-full object-cover" /> : <div className="h-full w-full bg-slate-100 text-[9px] uppercase grid place-items-center text-slate-500">{a.kind}</div>}</button>; })}
        {!f.options.length && <span className="text-xs text-slate-500">No released assets yet.</span>}
      </div>
    );
  else control = <Input {...common} type={f.type || "text"} step={f.step} value={value ?? ""} required={f.required} onChange={(e) => onChange(f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)} />;
  return (
    <div className={`space-y-1.5 ${f.full ? "sm:col-span-2" : ""}`}>
      <Label htmlFor={id} className="text-xs font-semibold text-slate-600">{f.label}</Label>
      {control}
    </div>
  );
}

export function FormDialog({ open, onOpenChange, title, description, fields, initial, onSubmit, submitLabel = "Save", testId = "form-dialog" }) {
  const [form, setForm] = useState(initial || {});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm(initial || {}); }, [open]); // eslint-disable-line
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await onSubmit(form); onOpenChange(false); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid={testId}>
        <DialogHeader><DialogTitle className="font-heading">{title}</DialogTitle>{description && <p className="text-sm text-slate-500">{description}</p>}</DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {fields.map((f) => <Field key={f.name} f={f} value={form[f.name]} onChange={(v) => setForm((s) => ({ ...s, [f.name]: v }))} />)}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid={`${testId}-cancel`}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid={`${testId}-submit`}>{busy ? "Saving…" : submitLabel}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function LineItemsEditor({ items, onChange, currency = "USD" }) {
  const upd = (i, k, v) => onChange(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const total = items.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.unit_price) || 0), 0);
  return (
    <div className="space-y-2" data-testid="line-items-editor">
      <div className="grid grid-cols-12 gap-2 table-head px-1"><div className="col-span-7">Item</div><div className="col-span-2">Qty</div><div className="col-span-3">Unit price</div></div>
      {items.map((it, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-center">
          <Input className="col-span-7" value={it.description} placeholder="Description" onChange={(e) => upd(i, "description", e.target.value)} data-testid={`item-desc-${i}`} />
          <Input className="col-span-2 num" type="number" step="any" value={it.qty} onChange={(e) => upd(i, "qty", e.target.value)} data-testid={`item-qty-${i}`} />
          <div className="col-span-3 flex gap-1">
            <Input className="num" type="number" step="any" value={it.unit_price} onChange={(e) => upd(i, "unit_price", e.target.value)} data-testid={`item-price-${i}`} />
            <button type="button" className="text-slate-400 hover:text-rose-600 px-1" onClick={() => onChange(items.filter((_, idx) => idx !== i))} data-testid={`item-remove-${i}`}>×</button>
          </div>
        </div>
      ))}
      <div className="flex items-center justify-between pt-1">
        <Button type="button" size="sm" variant="outline" onClick={() => onChange([...items, { description: "", qty: 1, unit_price: 0 }])} data-testid="add-line-item-btn">+ Add line</Button>
        <div className="text-sm">Total <span className="num font-semibold ml-2" data-testid="line-items-total">{new Intl.NumberFormat("en-US", { style: "currency", currency }).format(total)}</span></div>
      </div>
    </div>
  );
}

export const normalizeItems = (items) => items.filter((i) => i.description).map((i) => ({ description: i.description, qty: Number(i.qty) || 0, unit_price: Number(i.unit_price) || 0 }));

export const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function PdfButton({ href, label = "PDF", size = "sm", variant = "outline", className = "", testId }) {
  return (
    <Button type="button" size={size} variant={variant} className={className} title="Download PDF" aria-label="Download PDF" onClick={(e) => { e.stopPropagation(); window.open(`${API_BASE}${href}`, "_blank", "noopener"); }} data-testid={testId}>
      <FileDown size={14} className={label ? "mr-1" : ""} />{label}
    </Button>
  );
}

export function TimeLogDialog({ task, open, onOpenChange, onLogged, user }) {
  const localDate = () => { const d = new Date(); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 10); };
  const [form, setForm] = useState({ hours: 1, note: "", date: localDate() });
  const [entries, setEntries] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open && task) { setForm({ hours: 1, note: "", date: localDate() }); api.get(`/tasks/${task.id}/time`).then((r) => setEntries(r.data)).catch(() => setEntries([])); }
  }, [open, task]);
  if (!task) return null;
  const total = entries.reduce((s, e) => s + e.hours, 0);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post(`/tasks/${task.id}/time`, { hours: Number(form.hours), note: form.note, date: form.date || null }); setEntries([data, ...entries]); setForm({ ...form, hours: 1, note: "" }); toast.success(`${data.hours}h logged`); onLogged?.(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const remove = async (en) => { try { await api.delete(`/time/${en.id}`); setEntries(entries.filter((x) => x.id !== en.id)); onLogged?.(); } catch (err) { toast.error(errMsg(err)); } };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="time-log-dialog">
        <DialogHeader><DialogTitle className="font-heading">Log time · {task.title}</DialogTitle><p className="text-sm text-slate-500">{task.project_name}{task.estimate_hours ? ` · estimate ${task.estimate_hours}h` : ""} · logged <span className="num font-semibold text-slate-800" data-testid="time-total">{total.toFixed(1)}h</span></p></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-12 gap-2 items-end">
          <div className="col-span-3 space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Hours</Label><Input type="number" step="0.25" min="0.25" max="24" required value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} data-testid="time-hours-input" /></div>
          <div className="col-span-4 space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="time-date-input" /></div>
          <div className="col-span-5 space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Note</Label><Input value={form.note} placeholder="What did you work on?" onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="time-note-input" /></div>
          <Button type="submit" disabled={busy} className="col-span-12 bg-slate-900 hover:bg-slate-800" data-testid="time-log-submit">Log time</Button>
        </form>
        <div className="max-h-52 overflow-y-auto divide-y divide-slate-100 mt-2">
          {entries.map((en) => (
            <div key={en.id} className="flex items-center gap-3 py-2 text-sm" data-testid={`time-entry-${en.id}`}>
              <Avatar name={en.user_name || ""} src={en.user_picture} size={22} /><div className="flex-1 min-w-0"><span className="font-medium">{en.user_name}</span>{en.note && <span className="text-slate-500"> · {en.note}</span>}<div className="text-[11px] text-slate-400">{en.date}</div></div>
              <span className="num font-semibold">{en.hours}h</span>{(!user || user.role === "admin" || en.user_id === user.id) && <button type="button" onClick={() => remove(en)} className="text-slate-300 hover:text-rose-600" data-testid={`delete-time-${en.id}`}>×</button>}
            </div>
          ))}
          {!entries.length && <p className="text-xs text-slate-500 py-2">No time logged yet.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export const clientTitle = (c) => c?.brand_name || c?.company || c?.name || "";
export const clientSub = (c) => [c?.name, c?.company].filter(Boolean).join(" · ");
export const clientOptions = (clients) => clients.map((c) => ({ value: c.id, label: clientSub(c) ? `${clientTitle(c)} — ${clientSub(c)}` : clientTitle(c), client: c }));

